// Renders children at document.body via React portal, positioned with
// fixed coordinates derived from an anchor element. Useful for dropdowns
// and popovers that would otherwise be clipped by ancestor scroll/overflow
// containers (notably the booking modal's overflow:auto wrapper).
//
// Width matches the anchor width by default. Position re-measures on
// window scroll + resize so the portal tracks the anchor when the user
// scrolls the underlying page or resizes the window.

import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

interface AnchoredPortalProps {
  anchorRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  /** Vertical offset below the anchor, in px. Default 4. */
  offset?: number;
  /** Z-index for the portal layer. Default sits above the modal overlay. */
  zIndex?: number;
  children: ReactNode;
}

interface Position {
  top: number;
  left: number;
  width: number;
}

export function AnchoredPortal({
  anchorRef,
  isOpen,
  offset = 4,
  zIndex = 2147483641,
  children,
}: AnchoredPortalProps) {
  const [pos, setPos] = useState<Position | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + offset, left: r.left, width: r.width });
    };
    measure();

    // Re-measure on scroll/resize so the portal tracks the anchor. Use
    // capture on scroll so we catch scroll on any ancestor — including
    // the modal's overflow:auto wrapper — without needing to thread refs.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchorRef, isOpen, offset]);

  if (!isOpen || !pos) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
